/* Same-origin preview tabs intentionally retain their opener so Customize can return to the authenticated editor. */
/* eslint-disable react/jsx-no-target-blank */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { imageurl } from "../../../App";
import { resolveMediaUrl } from "../../../util/storeLogoHelper";
import { brandLogoForSurface } from "../../../assets/img/logo/brandLogos";
import { loadStoreSettings } from "../../../util/storeSettings";
import {
  FaDesktop,
  FaTabletAlt,
  FaMobileAlt,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaPlus,
  FaArrowUp,
  FaArrowDown,
  FaPalette,
  FaArrowLeft,
  FaCloudUploadAlt,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaCopy,
  FaUndoAlt,
  FaRedoAlt,
  FaSearch,
  FaShoppingBag,
  FaHeart,
  FaUser,
  FaGripVertical,
  FaBullhorn,
  FaImage,
  FaThLarge,
  FaColumns,
  FaTags,
  FaAlignLeft,
  FaEnvelope,
  FaEllipsisH,
  FaFont,
  FaChevronDown,
  FaCopyright,
  FaTimes,
  FaShoppingCart,
  FaPlayCircle,
  FaQuoteLeft,
  FaMagic,
} from "react-icons/fa";
import SectionRenderer from "../../../themes/SectionRenderer";
import {
  SECTION_DEFINITIONS,
  GLOBAL_SETTINGS_SCHEMA,
  createHeroSlide,
  resolveHeroSlides,
} from "../../../themes/themeRegistry";
import {
  readThemeListCache,
  readThemeRecord,
  writeActiveThemeCache,
  writeThemeListCache,
  writeThemeRecord,
} from "../../../themes/themeCache";
import { defaultHeaderMenu, visibleHeaderMenu } from "../../../themes/headerMenu";
import { defaultFooterMenus } from "../../../themes/footerMenu";
import { buildThemeDemoCatalog, prepareThemeForDemo } from "../../../themes/themeDemoData";
import { HeaderSettings } from "./HeaderMenuEditor";
import { FooterSettings } from "./FooterMenuEditor";
import { CartSettings, CartDrawerPreview } from "./CartMenuEditor";
import { CollectionSettings } from "./CollectionMenuEditor";
import { ProductSettings } from "./ProductMenuEditor";
import CollapseSection from "./CollapseSection";
import Footer from "../../../layouts/frontend/Footer";
import "../../../assets/admin/css/theme-editor.css";
import { useDesignStyle } from "../../../themes/designStyles";
import { checkImageFile, readApiErrors } from "../../../util/formErrors";

const SECTION_ICONS = {
  announcement_bar: FaBullhorn,
  hero_banner: FaImage,
  collection_list: FaThLarge,
  featured_products: FaShoppingBag,
  promo_banners: FaTags,
  image_with_text: FaColumns,
  image_banner: FaImage,
  rich_text: FaAlignLeft,
  newsletter: FaEnvelope,
  video: FaPlayCircle,
  review_slider: FaQuoteLeft,
};

// Icons for the Theme tab, by settings group id. Titles and hints live with
// the groups in GLOBAL_SETTINGS_SCHEMA.
const THEME_NAV_ICONS = {
  colors: FaPalette,
  typography: FaFont,
  buttons: FaShoppingBag,
  header: FaBullhorn,
  footer: FaCopyright,
  "side-cart": FaShoppingCart,
  "collections-page": FaLayerGroup,
  "collection-page": FaThLarge,
  "product-page": FaMobileAlt,
  "theme-style": FaMagic,
  "page-templates": FaThLarge,
};

const DEMO_PREVIEW_STORE = { storeName: "Your Store", logoUrl: "" };

const THEME_STYLE_PRESETS = [
  {
    key: "prestige",
    name: "Renown",
    description: "Elevated editorial spacing, portrait products, and a centered wordmark.",
    image: "/assets/themes/renown-showcase.webp",
    settings: {
      designStyle: "prestige",
      primaryColor: "#111111",
      secondaryColor: "#111111",
      accentColor: "#111111",
      backgroundColor: "#ffffff",
      surfaceColor: "#ffffff",
      textColor: "#111111",
      mutedTextColor: "#6f6f6f",
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      bodyFontFamily: "'Jost', 'Inter', sans-serif",
      buttonStyle: "sharp",
      buttonColor: "#111111",
      buttonTextColor: "#ffffff",
      productCardStyle: "prestige",
      productImageRatio: "portrait",
      pageWidth: "1440px",
      headerLayout: "prestige",
      headerBackground: "#ffffff",
      headerTextColor: "#111111",
      footerBackground: "#111111",
      footerHeadingColor: "#ffffff",
      footerTextColor: "#d0d0d0",
    },
  },
  {
    key: "showcase",
    name: "Electronics Store",
    description: "Product-first electronics presentation with bold orange accents and clear retail information.",
    image: "/assets/themes/electronics-showcase.webp",
    settings: {
      designStyle: "showcase",
      primaryColor: "#17140f",
      secondaryColor: "#17140f",
      accentColor: "#ff6900",
      backgroundColor: "#fbf9f6",
      surfaceColor: "#ffffff",
      textColor: "#17140f",
      mutedTextColor: "#6b655c",
      fontFamily: "'Bricolage Grotesque', 'Plus Jakarta Sans', sans-serif",
      bodyFontFamily: "'DM Sans', 'Inter', sans-serif",
      buttonStyle: "pill",
      buttonColor: "#ff6900",
      buttonTextColor: "#ffffff",
      productCardStyle: "showcase",
      productImageRatio: "square",
      pageWidth: "1200px",
      headerLayout: "classic",
      headerBackground: "#fbf9f6",
      headerTextColor: "#17140f",
      footerBackground: "#17140f",
      footerHeadingColor: "#ffffff",
      footerTextColor: "#d5cec4",
    },
  },
];

function ThemeStyleSettings({ activeStyle, currentThemeKey, onApply }) {
  const presetKey = currentThemeKey === "electronics" ? "showcase" : "prestige";
  const preset = THEME_STYLE_PRESETS.find((item) => item.key === presetKey);
  const active = activeStyle === presetKey;

  return (
    <div className="te-style-presets">
      <div className="te-style-intro">
        <FaMagic size={14} />
        <div><strong>Current theme style</strong><span>This is the design system for the theme you are customizing. Restore it without changing your store content or section order.</span></div>
      </div>
      {preset && (
          <button key={preset.key} type="button" className={`te-style-card ${active ? "is-active" : ""}`} onClick={() => onApply(preset)}>
            <img src={preset.image} alt="" />
            <span className="te-style-card-copy"><strong>{preset.name}</strong><small>{preset.description}</small></span>
            <span className="te-style-status">{active ? "Current theme" : "Restore style"}</span>
          </button>
      )}
    </div>
  );
}

function PreviewHeaderIcon({ children, onClick, label }) {
  if (onClick) {
    return (
      <button type="button" className="te-preview-icon" onClick={onClick} aria-label={label} title={label}>
        {children}
      </button>
    );
  }
  return <span className="te-preview-icon">{children}</span>;
}

function PreviewStoreMark({ storeSettings, surface = "#ffffff" }) {
  const name = storeSettings?.storeName || "Store";
  const src = storeSettings?.logoUrl
    ? storeSettings.logoUrl.startsWith("http")
      ? storeSettings.logoUrl
      : `${imageurl}/${storeSettings.logoUrl}`
    : brandLogoForSurface(surface);

  return <img src={src} alt={name} />;
}

function PreviewStoreHeader({ layout, viewportMode, storeSettings, globalSettings, onOpenCart }) {
  const desktop = viewportMode === "desktop";
  const menu = <PreviewMenuItems items={visibleHeaderMenu(globalSettings)} style={{ margin: 0 }} />;

  if (layout === "prestige") {
    const items = visibleHeaderMenu(globalSettings);
    return (
      <div
        className="te-preview-header is-prestige"
        style={{
          background: globalSettings.headerBackground || "#ffffff",
          color: globalSettings.headerTextColor || "#111111",
        }}
      >
        <div className="te-preview-prestige-side">
          {desktop ? <PreviewMenuItems items={items} /> : <span />}
        </div>
        <div className="te-preview-prestige-mark">{storeSettings?.storeName || "Store"}</div>
        <div className="te-preview-prestige-side is-right">
          <div className="te-preview-header-tools">
            <PreviewHeaderIcon>
              <FaUser size={14} />
            </PreviewHeaderIcon>
            <PreviewHeaderIcon>
              <FaSearch size={14} />
            </PreviewHeaderIcon>
            <PreviewHeaderIcon onClick={onOpenCart} label="Open side cart">
              <FaShoppingBag size={14} />
            </PreviewHeaderIcon>
          </div>
        </div>
      </div>
    );
  }

  if (layout === "logo-center") {
    return (
      <div
        className="te-preview-header is-logo-center"
        style={{
          background: globalSettings.headerBackground || "#ffffff",
          color: globalSettings.headerTextColor || "#111827",
        }}
      >
        <div className="te-preview-header-row">
          <span />
          <div className="te-preview-header-brand">
            <PreviewStoreMark storeSettings={storeSettings} surface={globalSettings.headerBackground} />
          </div>
          <div className="te-preview-header-tools">
            {!desktop && (
              <PreviewHeaderIcon>
                <FaSearch size={14} />
              </PreviewHeaderIcon>
            )}
            <PreviewHeaderIcon>
              <FaHeart size={14} />
            </PreviewHeaderIcon>
            <PreviewHeaderIcon>
              <FaUser size={14} />
            </PreviewHeaderIcon>
            <PreviewHeaderIcon onClick={onOpenCart} label="Open side cart">
              <FaShoppingBag size={14} />
            </PreviewHeaderIcon>
          </div>
        </div>
        {desktop && <div className="te-preview-header-menu">{menu}</div>}
      </div>
    );
  }

  if (layout === "centered") {
    return (
      <div
        className="te-preview-header is-centered"
        style={{
          background: globalSettings.headerBackground || "#0a0a0a",
          color: globalSettings.headerTextColor || "#f7f7f7",
        }}
      >
        <div className="te-preview-header-brand te-preview-wordmark">
          {storeSettings?.storeName || "Store"}
        </div>
        {desktop ? menu : <span />}
        <div className="te-preview-header-tools">
          <PreviewHeaderIcon>
            <FaSearch size={14} />
          </PreviewHeaderIcon>
          <PreviewHeaderIcon>
            <FaUser size={14} />
          </PreviewHeaderIcon>
          <PreviewHeaderIcon onClick={onOpenCart} label="Open side cart">
            <FaShoppingBag size={14} />
          </PreviewHeaderIcon>
        </div>
      </div>
    );
  }

  return (
    <div
      className="te-preview-header"
      style={{ backgroundColor: globalSettings.surfaceColor || "#ffffff" }}
    >
      <div className="te-preview-header-brand">
        <PreviewStoreMark storeSettings={storeSettings} surface={globalSettings.headerBackground} />
      </div>
      {desktop && (
        <div className="te-preview-header-search">
          <FaSearch size={13} />
          <span>Search catalog</span>
        </div>
      )}
      {desktop && menu}
      <div className="te-preview-header-tools">
        {!desktop && (
          <PreviewHeaderIcon>
            <FaSearch size={14} />
          </PreviewHeaderIcon>
        )}
        <PreviewHeaderIcon>
          <FaHeart size={14} />
        </PreviewHeaderIcon>
        <PreviewHeaderIcon>
          <FaUser size={14} />
        </PreviewHeaderIcon>
        <PreviewHeaderIcon onClick={onOpenCart} label="Open side cart">
          <FaShoppingBag size={14} />
        </PreviewHeaderIcon>
      </div>
    </div>
  );
}

function PreviewMenuItems({ items, nested = false, style }) {
  return (
    <ul className={nested ? "te-preview-submenu" : "te-preview-menu"} style={nested ? undefined : style}>
      {items.map((entry) => {
        const children = (entry.items || []).filter((item) => item.label && item.label.trim());
        return (
          <li key={entry.id}>
            <span>
              {entry.label}
              {children.length > 0 ? (nested ? " ›" : " ▾") : ""}
            </span>
            {children.length > 0 && <PreviewMenuItems items={children} nested />}
          </li>
        );
      })}
    </ul>
  );
}

function SectionGlyph({ type, size = 13 }) {
  // A section folder can set its own `icon`; older sections use the map above.
  const Icon = SECTION_DEFINITIONS.find((def) => def.type === type)?.icon || SECTION_ICONS[type] || FaLayerGroup;
  return <Icon size={size} />;
}

function toHexColor(value, fallback = "#111827") {
  if (typeof value !== "string") return fallback;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return ("#" + v[1] + v[1] + v[2] + v[2] + v[3] + v[3]).toLowerCase();
  }
  return fallback;
}

function HeaderChoiceGlyph({ layout }) {
  if (layout === "centered") {
    return (
      <span className="te-header-glyph is-dark" aria-hidden="true">
        <i className="mark" />
        <i className="links" />
        <i className="icons" />
      </span>
    );
  }
  if (layout === "prestige") {
    return (
      <span className="te-header-glyph" aria-hidden="true">
        <i className="links" />
        <i className="mark" />
        <i className="icons" />
      </span>
    );
  }
  if (layout === "logo-center") {
    return (
      <span className="te-header-glyph" aria-hidden="true">
        <i className="mark is-center" />
        <i className="links is-wide" />
      </span>
    );
  }
  return (
    <span className="te-header-glyph" aria-hidden="true">
      <i className="mark" />
      <i className="search" />
      <i className="icons" />
    </span>
  );
}

function FieldControl({ field, value, onChange, onUpload }) {
  if (field.type === "choice-cards") {
    const selected = value || field.options[0]?.value;
    return (
      <div className="te-field">
        <label className="te-label">{field.label}</label>
        <div className="te-choice-grid">
          {field.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`te-choice ${selected === opt.value ? "is-selected" : ""}`}
              onClick={() => onChange(opt.value)}
            >
              <HeaderChoiceGlyph layout={opt.value} />
              <span>{opt.label}</span>
              <small>{opt.hint}</small>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "color") {
    const color = toHexColor(value, field.fallback || "#111827");
    return (
      <div className="te-field">
        <label className="te-label">{field.label}</label>
        <div className="te-color-row">
          <label className="te-swatch" style={{ background: color }}>
            <input
              type="color"
              value={color}
              onChange={(e) => onChange(e.target.value)}
              aria-label={field.label}
            />
          </label>
          <input
            className="te-input te-mono"
            value={value || ""}
            placeholder={field.fallback || ""}
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        {field.hint ? <p className="te-sub">{field.hint}</p> : null}
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div className="te-field">
        <label className="te-label">{field.label}</label>
        <input
          className="te-input"
          type="number"
          min={field.min}
          max={field.max}
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className="te-field">
        <label className="te-label">{field.label}</label>
        <select
          className="te-select"
          value={value || field.options?.[0]?.value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="te-field">
        <label className="te-label">{field.label}</label>
        <textarea
          className="te-textarea"
          rows={3}
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === "toggle") {
    const on = value === undefined ? field.defaultOn !== false : value !== false && value !== "false";
    return (
      <div className="te-field">
        <div className="te-toggle-row">
          <span className="te-label">{field.label}</span>
          <button
            type="button"
            className={`te-switch ${on ? "is-on" : ""}`}
            role="switch"
            aria-checked={on}
            aria-label={field.label}
            onClick={() => onChange(!on)}
          />
        </div>
      </div>
    );
  }

  if (field.type === "image") {
    return (
      <div className="te-field">
        <label className="te-label">{field.label}</label>
        <div className="te-upload">
          {value ? (
            <div className="te-upload-preview">
              <img src={resolveMediaUrl(value)} alt="" />
            </div>
          ) : (
            <div className="te-upload-empty">No image yet</div>
          )}
          <div className="te-upload-row">
            <input
              className="te-input"
              placeholder="Paste an image URL"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
            />
            <button type="button" className="te-btn te-btn-ghost" onClick={onUpload}>
              <FaCloudUploadAlt size={13} />
              Upload
            </button>
          </div>
          {field.hint ? <p className="te-sub">{field.hint}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="te-field">
      <label className="te-label">{field.label}</label>
      <input
        className="te-input"
        type="text"
        value={value ?? ""}
        placeholder={field.placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function BannerImageSlots({ desktopValue, mobileValue, onChange, onUpload }) {
  const slot = (label, key, value, empty) => (
    <div className="te-photo-slot">
      <span className="te-label">{label}</span>
      <button type="button" className="te-photo-frame" onClick={() => onUpload(key)}>
        {value ? <img src={resolveMediaUrl(value)} alt="" /> : <span>{empty}</span>}
      </button>
      {value ? (
        <button type="button" className="te-photo-remove" onClick={() => onChange(key, "")}>
          Remove
        </button>
      ) : (
        <span className="te-photo-remove is-quiet">Click to upload</span>
      )}
    </div>
  );

  return (
    <div className="te-image-pair">
      {slot("Desktop", "imageUrl", desktopValue, "Add desktop")}
      {slot("Mobile", "mobileImageUrl", mobileValue, "Add mobile")}
      <p className="te-sub">Phones use the mobile photo. If it is empty, they use the desktop photo.</p>
    </div>
  );
}

function fieldVisible(field, settings) {
  if (!field.showWhen) return true;
  const current = settings?.[field.showWhen.key];
  if (Array.isArray(field.showWhen.values)) return field.showWhen.values.includes(current);
  return current === field.showWhen.value;
}

/**
 * Editor for a `type: "blocks"` field: a list of items (reviews, FAQ rows,
 * logos...) that merchants add, edit, reorder, and remove. The section's
 * definition describes the items, so no section needs its own editor code.
 *
 * field: { key, label, itemName, hint, max, fields, defaultItem,
 *          summary: { title, text } }   (title/text are item field keys)
 */
function BlocksEditor({ field, blocks, onChange, onUpload }) {
  const [openId, setOpenId] = useState(null);
  const itemName = field.itemName || "item";
  const atMax = field.max && blocks.length >= field.max;

  useEffect(() => {
    if (openId && !blocks.some((block) => block.id === openId)) setOpenId(null);
  }, [blocks, openId]);

  const update = (blockId, key, value) => {
    onChange(blocks.map((block) => (block.id === blockId ? { ...block, [key]: value } : block)));
  };
  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChange(next);
  };
  const add = () => {
    const block = { id: `${field.key}_${Date.now()}_${Math.floor(Math.random() * 1000)}`, ...(field.defaultItem || {}) };
    onChange([...blocks, block]);
    setOpenId(block.id);
  };

  return (
    <div className="te-field">
      {field.label ? <label className="te-label">{field.label}</label> : null}
      <div className="te-slides">
        {field.hint ? <p className="te-slide-hint">{field.hint}</p> : null}
        {blocks.length === 0 && <div className="te-slide-empty">Nothing here yet. Add a {itemName} to show it.</div>}
        {blocks.map((block, index) => {
          const isOpen = openId === block.id;
          const titleField = (field.fields || []).find((sub) => sub.key === field.summary?.title);
          const rawTitle = block[field.summary?.title];
          const optionLabel = titleField?.type === "select" ? titleField.options.find((opt) => String(opt.value) === String(rawTitle))?.label : null;
          const title = optionLabel || rawTitle || `${itemName.charAt(0).toUpperCase()}${itemName.slice(1)} ${index + 1}`;
          const text = block[field.summary?.text] || "";
          return (
            <article key={block.id || index} className={`te-footer-column ${isOpen ? "is-open" : ""}`}>
              <button
                type="button"
                className="te-footer-column-toggle"
                aria-expanded={isOpen}
                onClick={() => setOpenId((current) => (current === block.id ? null : block.id))}
              >
                <span className="te-footer-index">{index + 1}</span>
                <span className="te-footer-column-summary">
                  <strong>{title}</strong>
                  <small>{text || "\u00a0"}</small>
                </span>
                <FaChevronDown size={11} className="te-footer-chevron" />
              </button>
              {isOpen && (
                <div className="te-footer-links">
                  <div className="te-footer-tool-row">
                    <button type="button" className="te-footer-text-btn" disabled={index === 0} onClick={() => move(index, -1)}>Move up</button>
                    <button type="button" className="te-footer-text-btn" disabled={index === blocks.length - 1} onClick={() => move(index, 1)}>Move down</button>
                    <button
                      type="button"
                      className="te-footer-text-btn is-danger"
                      onClick={() => {
                        const next = blocks.filter((item) => item.id !== block.id);
                        onChange(next);
                        setOpenId(next[Math.min(index, next.length - 1)]?.id || null);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  {(field.fields || []).filter((sub) => fieldVisible(sub, block)).map((sub) => (
                    <FieldControl
                      key={sub.key}
                      field={sub}
                      value={block[sub.key]}
                      onChange={(value) => update(block.id, sub.key, value)}
                      onUpload={() => onUpload && onUpload(block.id, sub.key)}
                    />
                  ))}
                </div>
              )}
            </article>
          );
        })}
        <button type="button" className="te-add" onClick={add} disabled={atMax}>
          <FaPlus size={11} />
          {atMax ? `Up to ${field.max} ${itemName}s` : `Add ${itemName}`}
        </button>
      </div>
    </div>
  );
}

function SectionFields({ fields, settings, defaults, onChange, onUpload, onUploadBlock }) {
  const [sizeOpen, setSizeOpen] = useState(true);
  const merged = { ...defaults, ...settings };
  const visible = fields.filter((field) => fieldVisible(field, merged));
  const hasMobileBanner = visible.some((field) => field.key === "mobileImageUrl");
  const sizeFields = visible.filter((field) => field.group === "size");
  const rest = visible.filter(
    (field) =>
      field.group !== "size" &&
      !(hasMobileBanner && (field.key === "imageUrl" || field.key === "mobileImageUrl"))
  );
  const renderField = (field) => (field.type === "blocks" ? (
    <BlocksEditor
      key={field.key}
      field={field}
      blocks={Array.isArray(settings?.[field.key]) ? settings[field.key] : (defaults?.[field.key] || [])}
      onChange={(value) => onChange(field.key, value)}
      onUpload={onUploadBlock ? (blockId, subKey) => onUploadBlock(field.key, blockId, subKey) : null}
    />
  ) : (
    <FieldControl
      key={field.key}
      field={field}
      value={settings?.[field.key] ?? defaults?.[field.key]}
      onChange={(value) => onChange(field.key, value)}
      onUpload={() => onUpload(field.key)}
    />
  ));

  return (
    <>
      {rest.map(renderField)}
      {sizeFields.length > 0 && (
        <CollapseSection
          title="Size"
          text="Desktop height, phone height, and width"
          open={sizeOpen}
          onToggle={() => setSizeOpen((open) => !open)}
        >
          {sizeFields.map(renderField)}
        </CollapseSection>
      )}
    </>
  );
}

function HeroSlideFields({ slide, onChange, onUpload }) {
  const [openId, setOpenId] = useState("image");
  const toggle = (id) => setOpenId((current) => (current === id ? null : id));
  const set = (key) => (value) => onChange(key, value);

  return (
    <>
      <CollapseSection
        title="Image"
        text={slide.mobileImageUrl ? "Desktop and mobile photos" : slide.imageUrl ? "Desktop photo" : "No image yet"}
        open={openId === "image"}
        onToggle={() => toggle("image")}
      >
        <BannerImageSlots
          desktopValue={slide.imageUrl}
          mobileValue={slide.mobileImageUrl}
          onChange={(key, value) => onChange(key, value)}
          onUpload={onUpload}
        />
      </CollapseSection>
      <CollapseSection
        title="Text"
        text={slide.title || "Headline and description"}
        open={openId === "text"}
        onToggle={() => toggle("text")}
      >
        <FieldControl field={{ key: "badge", label: "Badge", type: "text" }} value={slide.badge} onChange={set("badge")} />
        <FieldControl field={{ key: "title", label: "Headline", type: "text" }} value={slide.title} onChange={set("title")} />
        <FieldControl
          field={{ key: "subtitle", label: "Description", type: "textarea" }}
          value={slide.subtitle}
          onChange={set("subtitle")}
        />
        <FieldControl
          field={{ key: "textColor", label: "Text color", type: "color", fallback: "#ffffff" }}
          value={slide.textColor}
          onChange={set("textColor")}
        />
      </CollapseSection>
      <CollapseSection
        title="Button"
        text={slide.ctaText || "No button"}
        open={openId === "button"}
        onToggle={() => toggle("button")}
      >
        <FieldControl field={{ key: "ctaText", label: "Button label", type: "text" }} value={slide.ctaText} onChange={set("ctaText")} />
        <FieldControl field={{ key: "ctaLink", label: "Button link", type: "text" }} value={slide.ctaLink} onChange={set("ctaLink")} />
        <FieldControl
          field={{ key: "buttonColor", label: "Button color", type: "color", fallback: "#2563eb" }}
          value={slide.buttonColor}
          onChange={set("buttonColor")}
        />
        <FieldControl
          field={{ key: "buttonTextColor", label: "Button text color", type: "color", fallback: "#111827" }}
          value={slide.buttonTextColor}
          onChange={set("buttonTextColor")}
        />
      </CollapseSection>
      <CollapseSection
        title="Second button"
        text={slide.secondaryCtaText || "Optional"}
        open={openId === "second"}
        onToggle={() => toggle("second")}
      >
        <FieldControl
          field={{ key: "secondaryCtaText", label: "Second button label", type: "text" }}
          value={slide.secondaryCtaText}
          onChange={set("secondaryCtaText")}
        />
        <FieldControl
          field={{ key: "secondaryCtaLink", label: "Second button link", type: "text" }}
          value={slide.secondaryCtaLink}
          onChange={set("secondaryCtaLink")}
        />
        <FieldControl
          field={{ key: "secondaryButtonColor", label: "Second button color", type: "color", fallback: "#ffffff" }}
          value={slide.secondaryButtonColor}
          onChange={set("secondaryButtonColor")}
        />
        <FieldControl
          field={{
            key: "secondaryButtonTextColor",
            label: "Second button text color",
            type: "color",
            fallback: "#ffffff",
          }}
          value={slide.secondaryButtonTextColor}
          onChange={set("secondaryButtonTextColor")}
        />
      </CollapseSection>
    </>
  );
}

function HeroSlideEditor({ slides, onChange, onUpload, onPreview }) {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!openId) return;
    if (slides.some((slide) => slide.id === openId)) return;
    setOpenId(null);
  }, [slides, openId]);

  const updateSlide = (slideId, key, value) => {
    onChange(slides.map((slide) => (slide.id === slideId ? { ...slide, [key]: value } : slide)));
  };

  const moveSlide = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= slides.length) return;
    const next = [...slides];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChange(next);
  };

  const toggleSlide = (slideId) => {
    setOpenId((current) => {
      const next = current === slideId ? null : slideId;
      if (next) onPreview?.(next);
      return next;
    });
  };

  const addSlide = () => {
    const slide = createHeroSlide();
    onChange([...slides, slide]);
    setOpenId(slide.id);
    onPreview?.(slide.id);
  };

  return (
    <div className="te-slides">
      <p className="te-slide-hint">Open a banner to edit it. They play one after another on the homepage.</p>

      {slides.length === 0 && (
        <div className="te-slide-empty">No banners yet. Add one to show it on the homepage.</div>
      )}

      {slides.map((slide, index) => {
        const isOpen = openId === slide.id;
        const preview = slide.ctaText || slide.subtitle || "No button";
        return (
          <article key={slide.id} className={`te-footer-column ${isOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="te-footer-column-toggle"
              aria-expanded={isOpen}
              onClick={() => toggleSlide(slide.id)}
            >
              <span className="te-footer-index">{index + 1}</span>
              {slide.imageUrl ? (
                <img className="te-slide-thumb" src={resolveMediaUrl(slide.imageUrl)} alt="" />
              ) : (
                <span className="te-slide-thumb te-slide-thumb-empty">
                  <FaImage size={12} />
                </span>
              )}
              <span className="te-footer-column-summary">
                <strong>{slide.title || "Untitled banner"}</strong>
                <small>{preview}</small>
              </span>
              <FaChevronDown size={11} className="te-footer-chevron" />
            </button>

            {isOpen && (
              <div className="te-footer-links">
                <div className="te-footer-tool-row">
                  <button type="button" className="te-footer-text-btn" disabled={index === 0} onClick={() => moveSlide(index, -1)}>
                    Move up
                  </button>
                  <button
                    type="button"
                    className="te-footer-text-btn"
                    disabled={index === slides.length - 1}
                    onClick={() => moveSlide(index, 1)}
                  >
                    Move down
                  </button>
                  <button
                    type="button"
                    className="te-footer-text-btn is-danger"
                    onClick={() => {
                      const next = slides.filter((item) => item.id !== slide.id);
                      onChange(next);
                      setOpenId(next[Math.min(index, next.length - 1)]?.id || null);
                    }}
                  >
                    Remove
                  </button>
                </div>
                <HeroSlideFields
                  slide={slide}
                  onChange={(key, value) => updateSlide(slide.id, key, value)}
                  onUpload={(key) => onUpload(slide.id, key || "imageUrl")}
                />
              </div>
            )}
          </article>
        );
      })}

      <button type="button" className="te-add" onClick={addSlide}>
        <FaPlus size={11} />
        Add banner
      </button>
    </div>
  );
}

// Settings groups that belong to one storefront page, and back.
const PAGE_FOR_GROUP = { "collections-page": "collections", "collection-page": "category", "product-page": "product" };
const GROUP_FOR_PAGE = { collections: "collections-page", category: "collection-page", product: "product-page" };

// "/collection" → collections, "/collection/x" → category, "/collection/x/y" → product, "/" → home.
function pageForPath(path) {
  const parts = path.split("?")[0].split("/").filter(Boolean);
  if (parts.length === 0) return "home";
  if (parts[0] !== "collection") return null;
  return ["collections", "category", "product"][Math.min(parts.length, 3) - 1];
}

// "best-selling" → "Best Selling", for the page switch.
function slugLabel(path, index) {
  const part = String(path || "").split("?")[0].split("/").filter(Boolean)[index] || "";
  const label = part.replace(/-+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  return label.length > 26 ? `${label.slice(0, 25).trim()}…` : label;
}

export default function ThemeEditor() {
  const history = useHistory();
  const location = useLocation();
  const themeKeyFromUrl = new URLSearchParams(location.search).get("theme");
  const contentModeFromUrl = new URLSearchParams(location.search).get("content");
  const cachedTheme = themeKeyFromUrl ? readThemeRecord(themeKeyFromUrl) : null;
  const cachedThemeList = readThemeListCache();
  const [currentThemeKey, setCurrentThemeKey] = useState(themeKeyFromUrl || cachedTheme?.theme_key || "prestige");
  const [themeChoices, setThemeChoices] = useState(cachedThemeList || []);
  const [themeData, setThemeData] = useState(cachedTheme);
  const [sections, setSections] = useState(cachedTheme?.sections || []);
  const [globalSettings, setGlobalSettings] = useState(cachedTheme?.settings || {});
  useDesignStyle(globalSettings?.designStyle);
  const [activeTab, setActiveTab] = useState("sections"); // 'sections' | 'settings'
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [viewportMode, setViewportMode] = useState("desktop"); // 'desktop' | 'tablet' | 'mobile'
  const [previewContentMode, setPreviewContentMode] = useState(contentModeFromUrl === "store" ? "store" : "demo");

  const [loading, setLoading] = useState(!cachedTheme);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const pastSnaps = useRef([]);
  const futureSnaps = useRef([]);
  const lastSnap = useRef(null);
  const baselineSnap = useRef(null);
  const skipHistory = useRef(false);
  const [historyTick, setHistoryTick] = useState(0);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [sectionQuery, setSectionQuery] = useState("");
  const [libraryQuery, setLibraryQuery] = useState("");
  const [libraryCategory, setLibraryCategory] = useState("All");
  const [selectedSettingCategory, setSelectedSettingCategory] = useState("colors");
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Real store data for preview
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const demoCatalog = useMemo(() => buildThemeDemoCatalog(currentThemeKey), [currentThemeKey]);
  const demoTheme = useMemo(
    () => prepareThemeForDemo({ sections, settings: globalSettings }, currentThemeKey),
    [sections, globalSettings, currentThemeKey]
  );
  const previewSections = previewContentMode === "demo" ? demoTheme.sections : sections;
  const previewCategories = previewContentMode === "demo" ? demoCatalog.categories : categories;
  const previewProducts = previewContentMode === "demo" ? demoCatalog.products : products;
  const previewBanners = previewContentMode === "demo" ? [] : banners;
  const previewStoreSettings = previewContentMode === "demo"
    ? (storeSettings || DEMO_PREVIEW_STORE)
    : storeSettings;

  const preventCanvasNavigation = useCallback((event) => {
    const link = event.target?.closest?.("a[href]");
    if (link) event.preventDefault();
  }, []);

  // File upload ref. The target is a ref so the chosen slide is still known
  // when the file dialog returns, before React re-renders.
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const canvasRef = useRef(null);
  const previewFrameRef = useRef(null);
  const scaleSlotRef = useRef(null);

  // Which page the preview shows. Collections, category, and product are the
  // real storefront pages in a frame, sent the unsaved theme live (nothing is
  // saved). Clicking inside the frame walks the store step by step:
  // Collections → a category → a product, and the settings panel follows.
  const [previewPage, setPreviewPage] = useState("home");
  const [frameSrc, setFrameSrc] = useState("");
  const [visited, setVisited] = useState({});
  const previewIframeRef = useRef(null);
  const framePageRef = useRef(null);
  const snapshotAttemptedRef = useRef("");

  const captureAndUploadSnapshot = useCallback(async () => {
    const source = previewFrameRef.current;
    if (!source || previewPage !== "home" || viewportMode !== "desktop") return null;

    if (document.fonts?.ready) await document.fonts.ready;
    const { default: html2canvas } = await import("html2canvas");
    const sourceWidth = Math.max(900, source.scrollWidth || source.clientWidth || 1280);
    const sourceHeight = Math.min(source.scrollHeight || 720, Math.round(sourceWidth * 9 / 16));
    const rendered = await html2canvas(source, {
      backgroundColor: globalSettings.backgroundColor || "#ffffff",
      useCORS: true,
      allowTaint: false,
      logging: false,
      scale: Math.min(1.25, 1280 / sourceWidth),
      width: sourceWidth,
      height: sourceHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDocument) => {
        const clone = clonedDocument.querySelector("[data-theme-snapshot-source]");
        if (clone) {
          clone.style.transform = "none";
          clone.style.margin = "0";
        }
      },
    });

    const output = document.createElement("canvas");
    output.width = 1280;
    output.height = 720;
    const context = output.getContext("2d");
    context.fillStyle = globalSettings.backgroundColor || "#ffffff";
    context.fillRect(0, 0, output.width, output.height);
    context.drawImage(rendered, 0, 0, output.width, output.height);

    const blob = await new Promise((resolve) => output.toBlob(resolve, "image/webp", 0.86));
    if (!blob) return null;
    const form = new FormData();
    form.append("snapshot", blob, `${currentThemeKey}-preview.webp`);
    const response = await axios.post(`/api/admin/themes/${currentThemeKey}/snapshot`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const previewImage = response.data?.preview_image;
    if (previewImage) {
      setThemeData((current) => ({ ...(current || {}), preview_image: previewImage }));
      setThemeChoices((current) => {
        const next = current.map((theme) => (
          theme.theme_key === currentThemeKey ? { ...theme, preview_image: previewImage } : theme
        ));
        writeThemeListCache(next);
        return next;
      });
    }
    return previewImage || null;
  }, [currentThemeKey, globalSettings.backgroundColor, previewPage, viewportMode]);

  useEffect(() => {
    if (loading || !themeData || themeData.preview_image || previewPage !== "home" || viewportMode !== "desktop") return undefined;
    if (snapshotAttemptedRef.current === currentThemeKey) return undefined;
    snapshotAttemptedRef.current = currentThemeKey;
    const timer = window.setTimeout(() => captureAndUploadSnapshot().catch(() => null), 1200);
    return () => window.clearTimeout(timer);
  }, [captureAndUploadSnapshot, currentThemeKey, loading, previewPage, themeData, viewportMode]);

  // A real collection (the fullest one, so the grid shows) and product from this store to preview with.
  const previewPaths = useMemo(() => {
    const collection = [...categories].sort((a, b) => Number(b.products_count || 0) - Number(a.products_count || 0))[0];
    const sellable = (item) => item.slug && item.category?.slug && Number(item.status) === 1 && Number(item.category?.status ?? 1) === 1;
    const product = products.find(sellable) || products.find((item) => item.slug && item.category?.slug);
    return {
      collections: "/collection",
      category: collection ? `/collection/${collection.slug}` : "/collection",
      product: product ? `/collection/${product.category.slug}/${product.slug}` : null,
    };
  }, [categories, products]);

  // The category or product last opened inside the preview wins over the defaults.
  const pathFor = useCallback((page) => visited[page] || previewPaths[page], [visited, previewPaths]);

  // Shows a page because the merchant asked for it (switch or settings group).
  const goToPage = useCallback((page) => {
    setPreviewPage(page);
    if (page === "home") {
      framePageRef.current = null;
      return;
    }
    const path = pathFor(page);
    if (!path) return;
    if (framePageRef.current !== page) {
      framePageRef.current = page;
      setFrameSrc(path);
    }
  }, [pathFor]);

  // Each page's settings group shows that page; the Sections tab shows home.
  useEffect(() => {
    if (activeTab === "settings" && PAGE_FOR_GROUP[selectedSettingCategory]) goToPage(PAGE_FOR_GROUP[selectedSettingCategory]);
    else if (activeTab === "sections") goToPage("home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSettingCategory, previewPaths.product]);

  const sendLivePreview = useCallback(() => {
    const frame = previewIframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      { type: "ecom-theme-preview", theme: { theme_key: currentThemeKey, settings: globalSettings, sections } },
      window.location.origin
    );
  }, [currentThemeKey, globalSettings, sections]);

  useEffect(() => {
    if (previewPage === "home") return undefined;
    const timer = setTimeout(sendLivePreview, 120);
    return () => clearTimeout(timer);
  }, [previewPage, sendLivePreview]);

  // Messages from the framed page: it is ready on a (new) page, or the
  // merchant clicked "Edit" on a part of it.
  useEffect(() => {
    const onMessage = (event) => {
      const frame = previewIframeRef.current;
      if (event.origin !== window.location.origin || !frame || event.source !== frame.contentWindow) return;
      const data = event.data || {};
      if (data.type === "ecom-preview-ready") {
        sendLivePreview();
        const page = pageForPath(String(data.path || ""));
        if (page === "home") {
          setActiveTab("sections");
          goToPage("home");
          return;
        }
        if (!page) return;
        framePageRef.current = page;
        setVisited((current) => (current[page] === data.path ? current : { ...current, [page]: data.path }));
        setPreviewPage(page);
        // Keep the panel on the page being shown when it was on a page group.
        if (activeTab === "settings" && PAGE_FOR_GROUP[selectedSettingCategory] && PAGE_FOR_GROUP[selectedSettingCategory] !== page) {
          setSelectedSettingCategory(GROUP_FOR_PAGE[page]);
        }
      }
      if (data.type === "ecom-edit-group" && GLOBAL_SETTINGS_SCHEMA.some((group) => group.id === data.group)) {
        setActiveTab("settings");
        setSelectedSettingCategory(data.group);
        setCartPreviewOpen(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [sendLivePreview, goToPage, activeTab, selectedSettingCategory]);
  const [fitScale, setFitScale] = useState(1);
  const [previewSlideId, setPreviewSlideId] = useState(null);

  // 1. Load theme data from backend
  const applyLoadedTheme = (theme, key) => {
    if (!theme) return;
    writeThemeRecord(theme);
    setThemeData(theme);
    setCurrentThemeKey(theme.theme_key || key);
    setSections(theme.sections || []);
    setGlobalSettings(theme.settings || {});
    if (theme.sections && theme.sections.length > 0) {
      setSelectedSectionId(theme.sections[0].id);
    }
    setHasUnsavedChanges(false);
    baselineSnap.current = lastSnap.current;
  };

  const loadThemeData = (key = "prestige") => {
    const saved = readThemeRecord(key);
    if (saved) {
      setThemeData(saved);
      setCurrentThemeKey(saved.theme_key || key);
      setSections(saved.sections || []);
      setGlobalSettings(saved.settings || {});
      setLoading(false);
    } else {
      setLoading(true);
    }
    axios
      .get(`/api/admin/themes/${key}?draft=1`)
      .then((res) => {
        if (res.data.status === 200 && res.data.theme) {
          applyLoadedTheme(res.data.theme, key);
        }
        setLoading(false);
      })
      .catch(() => {
        if (readThemeRecord(key)) {
          setLoading(false);
          return;
        }
        // Fallback to active theme or factory definition
        axios
          .get(`/api/active-theme?preview_theme=${key}`)
          .then((res) => {
            if (res.data.status === 200 && res.data.active_theme) {
              applyLoadedTheme(res.data.active_theme, key);
            }
            setLoading(false);
          })
          .catch(() => {
            // Server unreachable: use the Renown baseline so the editor still opens.
            const base = {
              name: "Renown",
              primaryColor: "#111111",
              accentColor: "#111111",
              cardStyle: "prestige",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            };
            setThemeData({
              theme_key: "prestige",
              name: base.name,
              version: "1.0.0",
              is_published: true,
            });
            setGlobalSettings({
              primaryColor: base.primaryColor,
              secondaryColor: "#0f172a",
              accentColor: base.accentColor,
              productCardStyle: base.cardStyle,
              fontFamily: base.fontFamily,
              buttonStyle: "rounded",
              pageWidth: "1280px",
              showAnnouncementBar: true,
              announcementBarText: "⚡ Free Express Delivery Nationwide On Orders Over Rs. 2,500",
              announcementBarBg: "#0f172a",
              announcementBarColor: "#ffffff",
              footerTagline: "Your premier destination for authentic curated goods.",
            });
            setSections([
              { id: "sec_announcement", type: "announcement_bar", enabled: true, settings: {} },
              { id: "sec_hero", type: "hero_banner", enabled: true, settings: {} },
              { id: "sec_collections", type: "collection_list", enabled: true, settings: {} },
              { id: "sec_featured_products", type: "featured_products", enabled: true, settings: {} },
              { id: "sec_promo", type: "promo_banners", enabled: true, settings: {} },
              { id: "sec_image_text", type: "image_with_text", enabled: true, settings: {} },
              { id: "sec_rich_text", type: "rich_text", enabled: true, settings: {} },
              { id: "sec_newsletter", type: "newsletter", enabled: true, settings: {} },
            ]);
            setLoading(false);
          });
      });
  };

  const refreshThemeChoices = () => {
    axios
      .get("/api/themes")
      .then((res) => {
        if (res.data.status === 200 && Array.isArray(res.data.themes)) {
          setThemeChoices(res.data.themes);
          writeThemeListCache(res.data.themes);
        }
      })
      .catch(() => {});
  };

  const switchTheme = (key) => {
    if (!key || key === currentThemeKey) return;
    if (hasUnsavedChanges && !window.confirm("This theme has unsaved edits. Leave without saving them?")) {
      return;
    }
    history.replace(`/admin/theme-editor?theme=${key}`);
  };

  useEffect(() => {
    document.title = "Theme editor";
    refreshThemeChoices();

    // Fetch real catalog & store data for center preview
    axios.get("/api/getcategory").then((res) => {
      if (res.data.status === 200) setCategories(res.data.category || []);
    });
    axios.get("/api/fetch-all-product?include_hidden=1").then((res) => {
      if (res.data.status === 200) {
        setProducts(res.data.products || res.data.data || []);
      }
    });
    axios
      .get("/api/banners")
      .then((res) => {
        if (res.data.status === 200 && res.data.banners) {
          setBanners(res.data.banners || []);
        }
      })
      .catch(() => {});
    loadStoreSettings().then((settings) => {
      if (settings && (settings.storeName || settings.logoUrl)) setStoreSettings(settings);
    });
  }, []);

  useEffect(() => {
    if (themeKeyFromUrl) {
      loadThemeData(themeKeyFromUrl);
      return undefined;
    }

    let mounted = true;
    axios
      .get("/api/themes")
      .then((res) => {
        if (!mounted) return;
        const themes = res.data.themes || [];
        setThemeChoices(themes);
        writeThemeListCache(themes);
        const live = themes.find((theme) => theme.is_published) || themes[0];
        history.replace(`/admin/theme-editor?theme=${live?.theme_key || "prestige"}`);
      })
      .catch(() => {
        if (mounted) loadThemeData("prestige");
      });

    return () => {
      mounted = false;
    };
  }, [themeKeyFromUrl]);

  // Unsaved changes warning on page refresh or navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (loading) {
      lastSnap.current = null;
      baselineSnap.current = null;
      pastSnaps.current = [];
      futureSnaps.current = [];
      return;
    }
    const snap = JSON.stringify({ sections, globalSettings });
    if (skipHistory.current) {
      skipHistory.current = false;
      lastSnap.current = snap;
      setHistoryTick((tick) => tick + 1);
      return;
    }
    if (lastSnap.current === null) {
      lastSnap.current = snap;
      baselineSnap.current = snap;
      return;
    }
    if (snap === lastSnap.current) return;
    pastSnaps.current.push(lastSnap.current);
    if (pastSnaps.current.length > 40) pastSnaps.current.shift();
    futureSnaps.current = [];
    lastSnap.current = snap;
    setHistoryTick((tick) => tick + 1);
  }, [sections, globalSettings, loading]);

  const applySnap = (raw) => {
    const data = JSON.parse(raw);
    skipHistory.current = true;
    setSections(Array.isArray(data.sections) ? data.sections : []);
    setGlobalSettings(data.globalSettings && typeof data.globalSettings === "object" ? data.globalSettings : {});
    setHasUnsavedChanges(raw !== baselineSnap.current);
  };

  const undoChange = () => {
    if (!pastSnaps.current.length || !lastSnap.current) return;
    futureSnaps.current.push(lastSnap.current);
    applySnap(pastSnaps.current.pop());
  };

  const redoChange = () => {
    if (!futureSnaps.current.length || !lastSnap.current) return;
    pastSnaps.current.push(lastSnap.current);
    applySnap(futureSnaps.current.pop());
  };

  // Section reordering: Up / Down arrows
  const moveSection = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sections.length) return;
    const updated = [...sections];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, moved);
    setSections(updated);
    setHasUnsavedChanges(true);
  };

  // Section reordering: Drag and Drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }
    const updated = [...sections];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, moved);
    setSections(updated);
    setDraggedIndex(null);
    setHasUnsavedChanges(true);
  };

  // Toggle section visibility
  const toggleSectionVisibility = (id) => {
    const updated = sections.map((sec) =>
      sec.id === id ? { ...sec, enabled: sec.enabled === false ? true : false } : sec
    );
    setSections(updated);
    setHasUnsavedChanges(true);
  };

  // Remove section
  const removeSection = (id) => {
    const updated = sections.filter((sec) => sec.id !== id);
    setSections(updated);
    if (selectedSectionId === id) {
      setSelectedSectionId(updated[0]?.id || null);
    }
    setHasUnsavedChanges(true);
  };

  // Duplicate section
  const duplicateSection = (sec) => {
    const settings = { ...sec.settings };
    if (Array.isArray(settings.reviews)) {
      settings.reviews = settings.reviews.map((review) => ({
        ...review,
        id: `review_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      }));
    }
    const newSection = {
      ...sec,
      id: `${sec.type}_${Date.now()}`,
      settings,
    };
    const index = sections.findIndex((s) => s.id === sec.id);
    const updated = [...sections];
    updated.splice(index + 1, 0, newSection);
    setSections(updated);
    setSelectedSectionId(newSection.id);
    setHasUnsavedChanges(true);
  };

  // Add section from library modal
  // Adds a section with its defaults, plus a preset's settings when one was picked.
  // Lists of blocks are copied with fresh ids so two copies never share items.
  const handleAddSection = (sectionDef, preset = null) => {
    const settings = JSON.parse(JSON.stringify({ ...sectionDef.defaultSettings, ...(preset?.settings || {}) }));
    (sectionDef.fields || []).forEach((field) => {
      if (field.type !== "blocks" || !Array.isArray(settings[field.key])) return;
      settings[field.key] = settings[field.key].map((item, index) => ({
        ...item,
        id: `${field.key}_${Date.now()}_${index}_${Math.floor(Math.random() * 1000)}`,
      }));
    });
    const newSection = {
      id: `${sectionDef.type}_${Date.now()}`,
      type: sectionDef.type,
      enabled: true,
      settings,
    };
    const updated = [...sections, newSection];
    setSections(updated);
    setSelectedSectionId(newSection.id);
    setShowAddSectionModal(false);
    setHasUnsavedChanges(true);
  };

  // Update section setting in real-time
  const handleSectionSettingChange = (fieldKey, value) => {
    const updated = sections.map((sec) => {
      if (sec.id === selectedSectionId) {
        return {
          ...sec,
          settings: {
            ...sec.settings,
            [fieldKey]: value,
          },
        };
      }
      return sec;
    });
    setSections(updated);
    setHasUnsavedChanges(true);
  };

  const updateHeroSlides = (producer) => {
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== selectedSectionId || sec.type !== "hero_banner") return sec;
        const current = Array.isArray(sec.settings?.slides)
          ? sec.settings.slides
          : resolveHeroSlides(sec.settings, banners);
        return {
          ...sec,
          settings: {
            ...sec.settings,
            slides: producer(current),
          },
        };
      })
    );
    setHasUnsavedChanges(true);
  };

  // Changes a blocks field (a list of items) on the selected section.
  const updateBlocks = (fieldKey, producer) => {
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== selectedSectionId) return sec;
        const def = SECTION_DEFINITIONS.find((item) => item.type === sec.type);
        const current = Array.isArray(sec.settings?.[fieldKey]) ? sec.settings[fieldKey] : (def?.defaultSettings?.[fieldKey] || []);
        return { ...sec, settings: { ...sec.settings, [fieldKey]: producer(current) } };
      })
    );    setHasUnsavedChanges(true);
  };


  // Update global theme setting in real-time
  const handleGlobalSettingChange = (key, value) => {
    setGlobalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const applyThemeStyle = (preset) => {
    setGlobalSettings((prev) => ({
      ...prev,
      ...preset.settings,
    }));
    if (preset.key === "prestige") {
      setSections((prev) => prev.map((section) => {
        if (section.type === "hero_banner") {
          return {
            ...section,
            settings: {
              ...section.settings,
              layout: "banner",
              alignment: "left",
              bannerHeight: "fullscreen",
              bannerWidth: "full",
              bannerFit: "cover",
              imageUrl: "/assets/themes/prestige-hero-v2.webp",
              mobileImageUrl: "/assets/themes/prestige-hero-v2.webp",
              textColor: "#ffffff",
              buttonColor: "#ffffff",
              buttonTextColor: "#111111",
            },
          };
        }
        if (section.type === "collection_list") {
          return {
            ...section,
            settings: { ...section.settings, layout: "grid-4", columns: 4, categoryCount: 4, showItemCount: false },
          };
        }
        if (section.type === "featured_products") {
          return {
            ...section,
            settings: { ...section.settings, columns: 4, count: Math.max(8, Number(section.settings?.count || 0)), aspectRatio: "portrait", showBadge: false },
          };
        }
        return section;
      }));
    }
    setHasUnsavedChanges(true);
  };

  // Save Draft (does not affect live public shoppers)
  const handleSaveDraft = () => {
    setSaving(true);
    axios
      .post(`/api/admin/themes/${currentThemeKey}/draft`, {
        settings: globalSettings,
        sections: sections,
      })
      .then((res) => {
        if (res.data.status === 200) {
          setHasUnsavedChanges(false);
          baselineSnap.current = lastSnap.current;
          captureAndUploadSnapshot().catch(() => null);
          swal("Draft Saved", `${themeData?.name || "This theme"} is saved. Other themes keep their own setup. Publish to show this one to shoppers.`, "success", {
            buttons: false,
            timer: 1600,
          });
        }
        setSaving(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || "Could not save draft.";
        swal("Save Notice", msg, "error");
        setSaving(false);
      });
  };

  // Preview shows the saved draft, so unsaved edits are saved first. The tab
  // opens right away so the browser does not block it as a pop-up.
  const handlePreview = (event) => {
    const url = `/theme-demo/${currentThemeKey}?draft=1`;
    if (!hasUnsavedChanges) return;
    event.preventDefault();
    const tab = window.open("", "_blank");
    setSaving(true);
    axios
      .post(`/api/admin/themes/${currentThemeKey}/draft`, { settings: globalSettings, sections })
      .then((res) => {
        if (res.data.status !== 200) throw new Error(res.data.message || "Could not save draft.");
        setHasUnsavedChanges(false);
        baselineSnap.current = lastSnap.current;
        if (tab) tab.location.href = url;
        else window.open(url, "_blank");
      })
      .catch((err) => {
        if (tab) tab.close();
        swal("Preview Notice", err.response?.data?.message || err.message || "Save the draft, then preview again.", "error");
      })
      .finally(() => setSaving(false));
  };

  // Publish live to storefront
  const handlePublishLive = () => {
    swal({
      title: `Publish ${themeData?.name || "this theme"}?`,
      text: "Shoppers will see this theme's saved colors, buttons, and homepage. Other themes keep their own saved setup.",
      icon: "warning",
      buttons: ["Cancel", "Yes, Publish Live"],
    }).then((willPublish) => {
      if (willPublish) {
        setSaving(true);
        axios
          .post(`/api/admin/themes/${currentThemeKey}/publish`, {
            settings: globalSettings,
            sections: sections,
          })
          .then((res) => {
            if (res.data.status === 200) {
              setHasUnsavedChanges(false);
              baselineSnap.current = lastSnap.current;
              captureAndUploadSnapshot().catch(() => null);
              setThemeData((current) => ({
                ...(current || {}),
                ...(res.data.theme || {}),
                theme_key: currentThemeKey,
                is_published: true,
              }));
              setThemeChoices((current) =>
                current.map((theme) => ({
                  ...theme,
                  is_published: theme.theme_key === currentThemeKey,
                }))
              );
              swal("Theme Live!", res.data.message || "Your storefront has been updated successfully.", "success");
              writeActiveThemeCache({
                theme_key: currentThemeKey,
                name: themeData?.name,
                is_published: true,
                settings: globalSettings,
                sections,
              });
              loadThemeData(currentThemeKey);
              refreshThemeChoices();
            }
            setSaving(false);
          })
          .catch((err) => {
            const msg = err.response?.data?.message || err.message || "Could not publish theme. Please try again.";
            swal("Publish Notice", msg, "error");
            setSaving(false);
          });
      }
    });
  };

  // Discard changes & reload from server
  const handleDiscardChanges = () => {
    if (!hasUnsavedChanges) {
      swal("Up to Date", "There are no unsaved changes to discard.", "info");
      return;
    }
    swal({
      title: "Discard Unsaved Changes?",
      text: "Revert all modifications made during this session back to your last saved version.",
      icon: "warning",
      buttons: ["Keep Editing", "Discard Changes"],
      dangerMode: true,
    }).then((willDiscard) => {
      if (willDiscard) {
        loadThemeData(currentThemeKey);
        swal("Changes Discarded", "Reverted to last saved configuration.", "success", {
          buttons: false,
          timer: 1200,
        });
      }
    });
  };

  // Restore factory defaults
  const handleResetDefaults = () => {
    swal({
      title: "Restore Default Configuration?",
      text: "This will reset all sections, brand colors, typography, and layout settings to the original factory template.",
      icon: "warning",
      buttons: ["Cancel", "Yes, Restore Defaults"],
      dangerMode: true,
    }).then((willReset) => {
      if (willReset) {
        setSaving(true);
        axios
          .post(`/api/admin/themes/${currentThemeKey}/reset`)
          .then((res) => {
            if (res.data.status === 200) {
              loadThemeData(currentThemeKey);
              swal("Defaults Restored", "Theme settings have been restored to factory defaults.", "success");
            }
            setSaving(false);
          })
          .catch(() => {
            loadThemeData("prestige");
            setSaving(false);
          });
      }
    });
  };

  // File upload for sections & global branding
  const triggerImageUpload = (key, isGlobal = false, slideId = null, block = null) => {
    uploadTargetRef.current = { key, isGlobal, slideId, block };
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = uploadTargetRef.current;
    if (!file || !target) return;
    // Same photo rules as the server, checked before uploading.
    const problem = checkImageFile(file, {
      maxMB: 5,
      types: ["image/jpeg", "image/png", "image/webp", "image/svg+xml"],
      extensions: /\.(jpe?g|png|webp|svg)$/i,
      allowed: "JPG, PNG, WebP or SVG",
    });
    if (problem) {
      swal("Photo not added", problem, "error");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    axios
      .post("/api/admin/themes/upload-asset", formData, {
        transformRequest: [
          (data, headers) => {
            if (headers) {
              delete headers["Content-Type"];
              delete headers["content-type"];
            }
            return data;
          },
        ],
      })
      .then((res) => {
        if (res.data.status === 200 && res.data.url) {
          if (target.slideId) {
            updateHeroSlides((current) =>
              current.map((slide) =>
                slide.id === target.slideId
                  ? { ...slide, [target.key || "imageUrl"]: res.data.url }
                  : slide
              )
            );
            setPreviewSlideId(target.slideId);
          } else if (target.block) {
            updateBlocks(target.block.field, (current) =>
              current.map((item) => (item.id === target.block.id ? { ...item, [target.key]: res.data.url } : item))
            );
          } else if (target.isGlobal) {
            handleGlobalSettingChange(target.key, res.data.url);
          } else {
            handleSectionSettingChange(target.key, res.data.url);
          }
          swal("Banner uploaded", "The preview is using this image.", "success", {
            buttons: false,
            timer: 1200,
          });
        } else {
          swal("Upload Error", res.data?.message || "Could not upload image.", "error");
        }
      })
      .catch((err) => {
        const { message, fields } = readApiErrors(err);
        swal("Upload Error", fields.image || message || "Could not upload image.", "error");
      });
  };

  // Selected section object & definition
  const selectedSection = sections.find((s) => s.id === selectedSectionId);
  const selectedSectionDef = SECTION_DEFINITIONS.find((d) => d.type === selectedSection?.type);

  // Viewport container width calculation
  const getCanvasWidth = () => {
    if (viewportMode === "mobile") return "390px";
    if (viewportMode === "tablet") return "768px";
    return globalSettings.pageWidth || "1280px";
  };

  useEffect(() => {
    if (!showAddSectionModal && !showMoreMenu) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        setShowAddSectionModal(false);
        setShowMoreMenu(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAddSectionModal, showMoreMenu]);

  useEffect(() => {
    if (!selectedSectionId || loading) return undefined;
    const canvas = canvasRef.current;
    const section = document.getElementById(selectedSectionId);
    if (!canvas || !section) return undefined;

    const canvasRect = canvas.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const header = canvas.querySelector(".storefront-canvas-header");
    const headerRect = header ? header.getBoundingClientRect() : null;
    const headerClearance = headerRect ? headerRect.height + 12 : 12;

    // The announcement sits directly under the store header. Scrolling it to the
    // top of the canvas hides the logo, search, and menu.
    if (headerRect && sectionRect.top <= headerRect.bottom + 24) {
      if (canvas.scrollTop > 0) {
        canvas.scrollTo({ top: 0, behavior: "smooth" });
      }
      return undefined;
    }

    const viewTop = canvasRect.top + headerClearance;
    const viewBottom = canvasRect.bottom - 24;
    if (sectionRect.top >= viewTop && sectionRect.bottom <= viewBottom) {
      return undefined;
    }

    const delta = sectionRect.top < viewTop
      ? sectionRect.top - viewTop
      : sectionRect.bottom - viewBottom;
    canvas.scrollTo({
      top: Math.max(0, canvas.scrollTop + delta),
      behavior: "smooth",
    });
    return undefined;
  }, [selectedSectionId, viewportMode, loading]);

  useEffect(() => {
    const row = document.querySelector(".te-list .te-section.is-active");
    const list = document.querySelector(".te-list");
    if (!row || !list) return undefined;
    const rowRect = row.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (rowRect.top < listRect.top) {
      list.scrollTop -= listRect.top - rowRect.top + 8;
    } else if (rowRect.bottom > listRect.bottom) {
      list.scrollTop += rowRect.bottom - listRect.bottom + 8;
    }
    return undefined;
  }, [selectedSectionId]);

  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return undefined;
    const measure = () => {
      if (viewportMode !== "desktop") {
        setFitScale(1);
        return;
      }
      const target = parseInt(String(globalSettings.pageWidth || "1280"), 10) || 1280;
      const available = Math.max(280, node.clientWidth - 36);
      const next = Math.min(1, available / target);
      setFitScale(Number(next.toFixed(3)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [viewportMode, globalSettings.pageWidth, loading]);

  useEffect(() => {
    const frame = previewFrameRef.current;
    const slot = scaleSlotRef.current;
    if (!frame || !slot || viewportMode !== "desktop") return undefined;
    const apply = () => {
      slot.style.height = `${Math.ceil(frame.offsetHeight * fitScale)}px`;
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [fitScale, viewportMode, loading, sections, globalSettings]);

  const selectedIndex = sections.findIndex((section) => section.id === selectedSectionId);
  const hiddenCount = sections.filter((section) => section.enabled === false).length;
  const query = sectionQuery.trim().toLowerCase();
  const outline = sections
    .map((sec, index) => ({ sec, index }))
    .filter(({ sec }) => {
      if (!query) return true;
      const def = SECTION_DEFINITIONS.find((item) => item.type === sec.type);
      const hay = `${def?.name || ""} ${sec.type} ${sec.settings?.title || ""} ${
        sec.settings?.heading || ""
      }`.toLowerCase();
      return hay.includes(query);
    });

  const libraryCategories = ["All", ...new Set(SECTION_DEFINITIONS.map((def) => def.category))];
  // Each section, then its presets (ready-made versions from its definition).
  const libraryItems = SECTION_DEFINITIONS.flatMap((def) => [
    { key: def.type, def, preset: null, name: def.name, label: def.category, description: def.description },
    ...(def.presets || []).map((preset, index) => ({
      key: `${def.type}__${index}`,
      def,
      preset,
      name: preset.name,
      label: `${def.name} preset`,
      description: preset.description || def.description,
    })),
  ]).filter((item) => {
    const inCategory = libraryCategory === "All" || item.def.category === libraryCategory;
    const librarySearch = libraryQuery.trim().toLowerCase();
    if (!librarySearch) return inCategory;
    return (
      inCategory &&
      `${item.name} ${item.description} ${item.def.category} ${item.def.name}`.toLowerCase().includes(librarySearch)
    );
  });

  const viewportLabel =
    viewportMode === "mobile"
      ? "Mobile · 390px"
      : viewportMode === "tablet"
      ? "Tablet · 768px"
      : `Desktop · ${globalSettings.pageWidth || "1280px"}`;

  const themeGroup = GLOBAL_SETTINGS_SCHEMA.find((group) => group.id === selectedSettingCategory) || GLOBAL_SETTINGS_SCHEMA[0];
  const themeNav = {
    title: themeGroup?.title || themeGroup?.category || "Theme",
    hint: themeGroup?.hint || "",
  };

  const openLibrary = () => {
    setLibraryQuery("");
    setLibraryCategory("All");
    setShowAddSectionModal(true);
  };

  return (
    <div className="te-studio">
      <header className="te-topbar">
        <div className="te-topbar-side">
          <Link
            to="/admin/themes"
            className="te-exit"
            title="All themes"
            onClick={(event) => {
              if (hasUnsavedChanges && !window.confirm("You have unsaved changes. Leave the editor?")) {
                event.preventDefault();
              }
            }}
          >
            <FaArrowLeft size={12} />
            Themes
          </Link>
          <div className="te-brand">
            <div className="te-brand-row">
              <select
                className="te-theme-select"
                aria-label="Choose theme"
                value={currentThemeKey}
                onChange={(event) => switchTheme(event.target.value)}
              >
                {themeChoices.length === 0 && (
                  <option value={currentThemeKey}>{themeData?.name || currentThemeKey}</option>
                )}
                {themeChoices.map((theme) => (
                  <option key={theme.theme_key} value={theme.theme_key}>
                    {theme.name}
                    {theme.is_published ? " · Live" : ""}
                  </option>
                ))}
              </select>
              <span className={`te-live-pill ${themeData?.is_published ? "is-live" : ""}`}>
                {themeData?.is_published ? "Live" : "Draft"}
              </span>
            </div>
            {hasUnsavedChanges && <span className="te-unsaved">Unsaved</span>}
          </div>
          <button type="button" className="te-icon-btn" onClick={undoChange} disabled={historyTick < 0 || pastSnaps.current.length === 0} title="Undo" aria-label="Undo">
            <FaUndoAlt size={13} />
          </button>
          <button type="button" className="te-icon-btn" onClick={redoChange} disabled={historyTick < 0 || futureSnaps.current.length === 0} title="Redo" aria-label="Redo">
            <FaRedoAlt size={13} />
          </button>
        </div>

        <div className="te-preview-tools">
        <div className="te-content-switch" role="group" aria-label="Preview content">
          <button
            type="button"
            className={previewContentMode === "demo" ? "is-active" : ""}
            onClick={() => setPreviewContentMode("demo")}
            title="Use the professional catalog shown in View demo"
          >
            Demo content
          </button>
          <button
            type="button"
            className={previewContentMode === "store" ? "is-active" : ""}
            onClick={() => setPreviewContentMode("store")}
            title="Use products and categories from your store"
          >
            My store
          </button>
        </div>
        <div className="te-devices" role="tablist" aria-label="Preview size">
          <button
            type="button"
            className={viewportMode === "desktop" ? "is-active" : ""}
            onClick={() => setViewportMode("desktop")}
            title="Desktop"
            aria-label="Desktop preview"
          >
            <FaDesktop size={14} />
          </button>
          <button
            type="button"
            className={viewportMode === "tablet" ? "is-active" : ""}
            onClick={() => setViewportMode("tablet")}
            title="Tablet, 768px"
            aria-label="Tablet preview"
          >
            <FaTabletAlt size={13} />
          </button>
          <button
            type="button"
            className={viewportMode === "mobile" ? "is-active" : ""}
            onClick={() => setViewportMode("mobile")}
            title="Mobile, 390px"
            aria-label="Mobile preview"
          >
            <FaMobileAlt size={13} />
          </button>
        </div>
        </div>

        <div className="te-topbar-side is-end">
          <a
            href={`/theme-demo/${currentThemeKey}?draft=1`}
            target="_blank"
            rel="opener"
            onClick={handlePreview}
            className="te-btn te-btn-ghost"
            title={hasUnsavedChanges ? "Save the draft and open it in a new tab" : "Open the saved draft in a new tab"}
          >
            <FaExternalLinkAlt size={11} />
            <span className="te-btn-label">Preview</span>
          </a>
          <button
            type="button"
            className="te-btn te-btn-ghost"
            onClick={handleSaveDraft}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="te-btn te-btn-primary"
            onClick={handlePublishLive}
            disabled={saving}
          >
            {saving ? "Publishing…" : "Publish"}
          </button>
          <div className="te-menu-wrap">
            <button
              type="button"
              className="te-icon-btn"
              aria-label="More actions"
              aria-expanded={showMoreMenu}
              onClick={() => setShowMoreMenu((open) => !open)}
            >
              <FaEllipsisH size={14} />
            </button>
            {showMoreMenu && (
              <>
                <button
                  type="button"
                  className="te-menu-backdrop"
                  aria-label="Close menu"
                  onClick={() => setShowMoreMenu(false)}
                />
                <div className="te-menu" role="menu">
                  <button
                    type="button"
                    disabled={!hasUnsavedChanges}
                    onClick={() => {
                      setShowMoreMenu(false);
                      handleDiscardChanges();
                    }}
                  >
                    <FaUndoAlt size={12} />
                    Discard changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      handleResetDefaults();
                    }}
                  >
                    Restore defaults
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="te-file-input"
      />

      <div className="te-workspace">
        <aside className="te-panel">
          <div className="te-tabs" role="tablist">
            <button
              type="button"
              className={activeTab === "sections" ? "is-active" : ""}
              onClick={() => setActiveTab("sections")}
            >
              Sections
            </button>
            <button
              type="button"
              className={activeTab === "settings" ? "is-active" : ""}
              onClick={() => setActiveTab("settings")}
            >
              Theme
            </button>
          </div>

          {activeTab === "sections" ? (
            <>
              <div className="te-panel-head">
                <span className="te-kicker">Home page</span>
                <span className="te-sub">
                  {sections.length} {sections.length === 1 ? "section" : "sections"}
                  {hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ""}
                </span>
              </div>
              <label className="te-search">
                <FaSearch size={12} />
                <input
                  value={sectionQuery}
                  onChange={(event) => setSectionQuery(event.target.value)}
                  placeholder="Find a section"
                />
              </label>
              <div className="te-list">
                {outline.length === 0 && (
                  <div className="te-empty-list">No sections match that search.</div>
                )}
                {outline.map(({ sec, index }) => {
                  const def = SECTION_DEFINITIONS.find((item) => item.type === sec.type);
                  const isSelected = selectedSectionId === sec.id;
                  const subtitle =
                    sec.enabled === false
                      ? "Hidden"
                      : sec.settings?.title || sec.settings?.heading || def?.category || "Section";
                  return (
                    <div
                      key={sec.id}
                      role="button"
                      tabIndex={0}
                      draggable={!query}
                      onDragStart={(event) => {
                        if (event.target.closest("button")) {
                          event.preventDefault();
                          return;
                        }
                        handleDragStart(event, index);
                      }}
                      onDragOver={(event) => handleDragOver(event, index)}
                      onDrop={(event) => handleDrop(event, index)}
                      onDragEnd={handleDragEnd}
                      onClick={() => setSelectedSectionId(sec.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedSectionId(sec.id);
                        }
                      }}
                      className={`te-section ${isSelected ? "is-active" : ""} ${
                        sec.enabled === false ? "is-hidden" : ""
                      } ${draggedIndex === index ? "is-dragging" : ""} ${
                        dragOverIndex === index ? "is-drop" : ""
                      }`}
                    >
                      <span className="te-grip" title="Drag to reorder">
                        <FaGripVertical size={12} />
                      </span>
                      <span className="te-section-icon">
                        <SectionGlyph type={sec.type} />
                      </span>
                      <span className="te-section-copy">
                        <span className="te-section-name">{def?.name || sec.type.replace(/_/g, " ")}</span>
                        <span className="te-section-meta">{subtitle}</span>
                      </span>
                      <span className="te-section-actions">
                        <button
                          type="button"
                          className="te-mini"
                          title="Duplicate"
                          onClick={(event) => {
                            event.stopPropagation();
                            duplicateSection(sec);
                          }}
                        >
                          <FaCopy size={11} />
                        </button>
                        <button
                          type="button"
                          className="te-mini"
                          title={sec.enabled === false ? "Show section" : "Hide section"}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSectionVisibility(sec.id);
                          }}
                        >
                          {sec.enabled === false ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                        </button>
                        <button
                          type="button"
                          className="te-mini is-danger"
                          title="Remove section"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeSection(sec.id);
                          }}
                        >
                          <FaTrash size={11} />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
              <button type="button" className="te-add" onClick={openLibrary}>
                <FaPlus size={11} />
                Add section
              </button>
            </>
          ) : (
            <>
              <div className="te-panel-head">
                <span className="te-kicker">Theme settings</span>
                <span className="te-sub">Applied across the storefront</span>
              </div>
              <div className="te-list">
                {GLOBAL_SETTINGS_SCHEMA.map((category) => {
                  const Icon = THEME_NAV_ICONS[category.id] || FaPalette;
                  const active = selectedSettingCategory === category.id;
                  return (
                    <button
                      key={category.category}
                      type="button"
                      className={`te-section ${active ? "is-active" : ""}`}
                      onClick={() => {
                        setSelectedSettingCategory(category.id);
                        setCartPreviewOpen(category.id === "side-cart");
                      }}
                    >
                      <span className="te-section-icon">
                        <Icon size={13} />
                      </span>
                      <span className="te-section-copy">
                        <span className="te-section-name">{category.title || category.category}</span>
                        <span className="te-section-meta">
                          {category.hint || `${category.fields.length} settings`}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="te-note">
                Logo, store name, and favicon live in{" "}
                <Link to="/admin/settings" target="_blank">
                  Store settings
                </Link>
                .
              </div>
            </>
          )}
        </aside>

        <main className="te-canvas" ref={canvasRef}>
          {loading ? (
            <div className="te-loading">
              <div className="te-spinner" />
              Loading theme
            </div>
          ) : (
            <div
              className={`te-stage${viewportMode === "desktop" ? "" : " is-device"}`}
              style={viewportMode === "desktop" ? undefined : { width: getCanvasWidth() }}
            >
              <div className="te-stage-meta">
                <div className="te-page-switch" role="tablist" aria-label="Page to preview">
                  {[
                    ["home", "Home"],
                    ["collections", "Collections"],
                    ["category", visited.category ? `Category · ${slugLabel(visited.category, 1)}` : "Category"],
                    ["product", visited.product ? `Product · ${slugLabel(visited.product, 2)}` : "Product"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={previewPage === value}
                      className={previewPage === value ? "is-active" : ""}
                      disabled={value === "product" && !pathFor("product")}
                      title={value === "home" ? "Home page" : pathFor(value) || ""}
                      onClick={() => {
                        if (value === "home") setActiveTab("sections");
                        else if (activeTab === "settings" && PAGE_FOR_GROUP[selectedSettingCategory]) setSelectedSettingCategory(GROUP_FOR_PAGE[value]);
                        goToPage(value);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span>{viewportLabel}</span>
              </div>
              <div
                className="te-scale-slot"
                ref={scaleSlotRef}
                style={
                  viewportMode === "desktop"
                    ? {
                        width: `${Math.round(
                          (parseInt(String(globalSettings.pageWidth || "1280"), 10) || 1280) * fitScale
                        )}px`,
                      }
                    : undefined
                }
              >
              <div
                ref={previewFrameRef}
                data-theme-snapshot-source
                data-design={globalSettings.designStyle || undefined}
                onClickCapture={preventCanvasNavigation}
                className={`te-frame te-frame-${viewportMode}${
                  viewportMode === "desktop" && fitScale < 0.995 ? " is-scaled" : ""
                }`}
                style={
                  viewportMode === "desktop"
                    ? {
                        width: getCanvasWidth(),
                        transform: fitScale < 0.995 ? `scale(${fitScale})` : undefined,
                      }
                    : undefined
                }
              >
                {viewportMode === "mobile" && (
                  <div className="te-notch">
                    <span />
                  </div>
                )}

                {previewPage === "home" ? (
                  <>
                  <div className="storefront-canvas-header">
                    <PreviewStoreHeader
                      layout={globalSettings.headerLayout || "classic"}
                      viewportMode={viewportMode}
                      storeSettings={previewStoreSettings}
                      globalSettings={globalSettings}
                      onOpenCart={() => setCartPreviewOpen(true)}
                    />
                  </div>

                  <SectionRenderer
                    sections={previewSections}
                    themeKey={currentThemeKey}
                    globalSettings={globalSettings}
                    categories={previewCategories}
                    products={previewProducts}
                    banners={previewBanners}
                    storeSettings={previewStoreSettings}
                    previewSlideId={previewSlideId}
                    viewportMode={viewportMode}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={(id) => {
                      setSelectedSectionId(id);
                      setActiveTab("sections");
                    }}
                    isEditor={true}
                  />

                  <Footer themeSettings={globalSettings} embedded storeSettingsOverride={previewStoreSettings} />
                  {cartPreviewOpen && (
                    <CartDrawerPreview
                      settings={globalSettings}
                      onClose={() => setCartPreviewOpen(false)}
                    />
                  )}
                  </>
                ) : (
                  <iframe
                    key={currentThemeKey}
                    ref={previewIframeRef}
                    title={`${previewPage.charAt(0).toUpperCase()}${previewPage.slice(1)} page preview`}
                    className={`te-page-frame te-page-frame-${viewportMode}`}
                    src={`${frameSrc || "/collection"}?preview_theme=${encodeURIComponent(currentThemeKey)}`}
                    onLoad={sendLivePreview}
                  />
                )}
              </div>
              </div>
            </div>
          )}
        </main>

        <aside className="te-panel te-panel-right">
          {activeTab === "settings" ? (
            <div className="d-flex flex-column h-100">
              <div className="te-inspector-head">
                <div className="te-inspector-kicker">Theme</div>
                <h2 className="te-inspector-title">{themeNav.title}</h2>
                {themeNav.hint && <p className="te-inspector-desc">{themeNav.hint}</p>}
              </div>
              <div className="te-inspector-body">
                {selectedSettingCategory === "theme-style" ? (
                  <ThemeStyleSettings
                    currentThemeKey={currentThemeKey}
                    activeStyle={
                      globalSettings.designStyle
                      || (currentThemeKey === "electronics" ? "showcase" : "prestige")
                    }
                    onApply={applyThemeStyle}
                  />
                ) : selectedSettingCategory === "product-page" ? (
                  <ProductSettings
                    fields={themeGroup?.fields || []}
                    settings={globalSettings}
                    renderField={(field) => field.type === "blocks" ? (
                      <BlocksEditor
                        field={field}
                        blocks={Array.isArray(globalSettings[field.key]) ? globalSettings[field.key] : (field.defaultValue || [])}
                        onChange={(value) => handleGlobalSettingChange(field.key, value)}
                      />
                    ) : (
                      <FieldControl
                        field={field}
                        value={globalSettings[field.key]}
                        onChange={(value) => handleGlobalSettingChange(field.key, value)}
                        onUpload={() => triggerImageUpload(field.key, true)}
                      />
                    )}
                  />
                ) : selectedSettingCategory === "collection-page" ? (
                  <CollectionSettings
                    fields={themeGroup?.fields || []}
                    settings={globalSettings}
                    renderField={(field) => (
                      <FieldControl
                        field={field}
                        value={globalSettings[field.key]}
                        onChange={(value) => handleGlobalSettingChange(field.key, value)}
                        onUpload={() => triggerImageUpload(field.key, true)}
                      />
                    )}
                  />
                ) : selectedSettingCategory === "side-cart" ? (
                  <CartSettings
                    fields={themeGroup?.fields || []}
                    settings={globalSettings}
                    menus={Array.isArray(globalSettings.cartMenus) ? globalSettings.cartMenus : []}
                    onMenus={(next) => handleGlobalSettingChange("cartMenus", next)}
                    renderField={(field) => (
                      <FieldControl
                        field={field}
                        value={globalSettings[field.key]}
                        onChange={(value) => handleGlobalSettingChange(field.key, value)}
                        onUpload={() => triggerImageUpload(field.key, true)}
                      />
                    )}
                  />
                ) : selectedSettingCategory === "footer" ? (
                  <FooterSettings
                    fields={themeGroup?.fields || []}
                    settings={globalSettings}
                    menus={
                      Array.isArray(globalSettings.footerMenus)
                        ? globalSettings.footerMenus
                        : defaultFooterMenus()
                    }
                    onMenus={(next) => handleGlobalSettingChange("footerMenus", next)}
                    renderField={(field) => (
                      <FieldControl
                        field={field}
                        value={globalSettings[field.key]}
                        onChange={(value) => handleGlobalSettingChange(field.key, value)}
                        onUpload={() => triggerImageUpload(field.key, true)}
                      />
                    )}
                  />
                ) : selectedSettingCategory === "header" ? (
                  <HeaderSettings
                    fields={themeGroup?.fields || []}
                    settings={globalSettings}
                    menus={
                      Array.isArray(globalSettings.headerMenus)
                        ? globalSettings.headerMenus
                        : defaultHeaderMenu()
                    }
                    onMenus={(next) => handleGlobalSettingChange("headerMenus", next)}
                    renderField={(field) => (
                      <FieldControl
                        field={field}
                        value={globalSettings[field.key]}
                        onChange={(value) => handleGlobalSettingChange(field.key, value)}
                        onUpload={() => triggerImageUpload(field.key, true)}
                      />
                    )}
                  />
                ) : (
                  themeGroup?.fields.map((field) => (
                    <FieldControl
                      key={field.key}
                      field={field}
                      value={globalSettings[field.key]}
                      onChange={(value) => handleGlobalSettingChange(field.key, value)}
                      onUpload={() => triggerImageUpload(field.key, true)}
                    />
                  ))
                )}
              </div>
            </div>
          ) : selectedSection && selectedSectionDef ? (
            <div className="d-flex flex-column h-100">
              <div className="te-inspector-head">
                <div className="te-inspector-kicker-row">
                  <div className="te-inspector-kicker">{selectedSectionDef.category}</div>
                  <div className="te-inspector-tools">
                    <button
                      type="button"
                      className="te-mini"
                      title="Move up"
                      disabled={selectedIndex <= 0}
                      onClick={() => moveSection(selectedIndex, -1)}
                    >
                      <FaArrowUp size={11} />
                    </button>
                    <button
                      type="button"
                      className="te-mini"
                      title="Move down"
                      disabled={selectedIndex < 0 || selectedIndex >= sections.length - 1}
                      onClick={() => moveSection(selectedIndex, 1)}
                    >
                      <FaArrowDown size={11} />
                    </button>
                    <button
                      type="button"
                      className="te-mini"
                      title="Duplicate"
                      onClick={() => duplicateSection(selectedSection)}
                    >
                      <FaCopy size={11} />
                    </button>
                    <button
                      type="button"
                      className="te-mini is-danger"
                      title="Remove"
                      onClick={() => removeSection(selectedSection.id)}
                    >
                      <FaTrash size={11} />
                    </button>
                  </div>
                </div>
                <h2 className="te-inspector-title">{selectedSectionDef.name}</h2>
                <p className="te-inspector-desc">{selectedSectionDef.description}</p>
              </div>
              <div className="te-inspector-body">
                {selectedSectionDef.fields?.some((field) => field.key === "mobileImageUrl") && (
                  <BannerImageSlots
                    desktopValue={selectedSection.settings?.imageUrl || ""}
                    mobileValue={selectedSection.settings?.mobileImageUrl || ""}
                    onChange={(key, value) => handleSectionSettingChange(key, value)}
                    onUpload={(key) => triggerImageUpload(key, false)}
                  />
                )}
                <div className="te-visibility">
                  <div>
                    <strong>Visible on storefront</strong>
                    <span>
                      {selectedSection.enabled === false
                        ? "Hidden from shoppers"
                        : "Shown on the home page"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`te-switch ${selectedSection.enabled !== false ? "is-on" : ""}`}
                    role="switch"
                    aria-checked={selectedSection.enabled !== false}
                    aria-label="Visible on storefront"
                    onClick={() => toggleSectionVisibility(selectedSection.id)}
                  />
                </div>
                <SectionFields
                  fields={selectedSectionDef.fields}
                  settings={selectedSection.settings}
                  defaults={selectedSectionDef.defaultSettings}
                  onChange={handleSectionSettingChange}
                  onUpload={(key) => triggerImageUpload(key, false)}
                  onUploadBlock={(field, blockId, key) => triggerImageUpload(key, false, null, { field, id: blockId })}
                />
                {selectedSection.type === "hero_banner" && (
                  <HeroSlideEditor
                    slides={
                      Array.isArray(selectedSection.settings?.slides)
                        ? selectedSection.settings.slides
                        : resolveHeroSlides(selectedSection.settings, banners)
                    }
                    onChange={(next) => updateHeroSlides(() => next)}
                    onUpload={(slideId, key = "imageUrl") => triggerImageUpload(key, false, slideId)}
                    onPreview={setPreviewSlideId}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="te-empty">
              <div className="te-empty-mark">
                <FaLayerGroup size={16} />
              </div>
              <h3>No section selected</h3>
              <p>Choose a section from the outline, or click a block in the preview to edit it.</p>
            </div>
          )}
        </aside>
      </div>

      {showAddSectionModal && (
        <div
          className="te-modal-backdrop"
          onClick={() => setShowAddSectionModal(false)}
          role="presentation"
        >
          <div
            className="te-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="te-library-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="te-modal-head">
              <div>
                <h2 id="te-library-title">Add a section</h2>
                <p>Insert a block at the bottom of the home page. You can reorder it after.</p>
              </div>
              <button
                type="button"
                className="te-icon-btn"
                aria-label="Close"
                onClick={() => setShowAddSectionModal(false)}
              >
                <FaTimes size={14} />
              </button>
            </div>
            <label className="te-search te-modal-search">
              <FaSearch size={12} />
              <input
                value={libraryQuery}
                onChange={(event) => setLibraryQuery(event.target.value)}
                placeholder="Search sections"
                autoFocus
              />
            </label>
            <div className="te-chips">
              {libraryCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`te-chip ${libraryCategory === category ? "is-active" : ""}`}
                  onClick={() => setLibraryCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="te-lib">
              {libraryItems.length === 0 && (
                <div className="te-lib-empty">No sections match that search.</div>
              )}
              {libraryItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="te-lib-card"
                  onClick={() => handleAddSection(item.def, item.preset)}
                >
                  <span className="te-lib-icon">
                    <SectionGlyph type={item.def.type} size={15} />
                  </span>
                  <span>
                    <strong>{item.name}</strong>
                    <em>{item.label}</em>
                    <p>{item.description}</p>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
